/**
 * @fileoverview Shared Person schema used by both frontend and backend to ensure
 * validation rules stay perfectly synchronized.
 */

import { z } from 'zod';
import { archivableAccountTrackingFields, archivedAtSchema, commonValidators, createCrudSchemas, createEntityIdSchema, createdAtSchema, deletedAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { ID_PATTERNS } from './types.js';

/**
 * Person validation schema.
 * Used for both create and update operations.
 */
export const PersonSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.PERSON, 'ID must be in pe-prefixed format'),
  first_name: commonValidators.requiredString('First Name', 50),
  last_name: commonValidators.optionalString(50),
  dob: commonValidators.dateString(),
  email: commonValidators.email(),
  phone: commonValidators.optionalString(100),
  bio: commonValidators.optionalString(1000),
  aspects: commonValidators.optionalString(1000),
  avatar: commonValidators.optionalUrl('Avatar'),
  job_title: commonValidators.optionalString(100),
  department: commonValidators.optionalString(100),
  facebook: commonValidators.optionalUrl('Facebook'),
  linkedin: commonValidators.optionalUrl('LinkedIn'),
  github: commonValidators.optionalUrl('GitHub'),
  instagram: commonValidators.optionalUrl('Instagram'),
  website: commonValidators.optionalUrl('Website'),
  manager_id: commonValidators.foreignKey('Manager ID'),
  area_id: commonValidators.foreignKey('Area ID'),
  organisation_id: commonValidators.foreignKey('Organisation ID'),
  address_line1: commonValidators.optionalString(255),
  address_line2: commonValidators.optionalString(255),
  city_town: commonValidators.optionalString(100),
  state_county: commonValidators.optionalString(100),
  postal_code: commonValidators.optionalString(20),
  country_code: commonValidators.countryCode(),
  is_pinned: commonValidators.booleanWithDefault(false),
  is_vip: commonValidators.booleanWithDefault(false),
  metadata: commonValidators.jsonField('Metadata'),
  ...archivableAccountTrackingFields,
  archived_at: archivedAtSchema,
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

const { CreateSchema, UpdateSchema } = createCrudSchemas(PersonSchema);

export const PersonCreateSchema = CreateSchema;
export const PersonUpdateSchema = UpdateSchema;

export type Person = z.infer<typeof PersonSchema>;
export type PersonCreate = z.infer<typeof PersonCreateSchema>;
export type PersonUpdate = z.infer<typeof PersonUpdateSchema>;

export const PERSON_FIELDS = getSchemaFields(PersonSchema);
